import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ArrowLeft, Pencil, User, Calendar as CalendarIcon, Clock, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";

type Errors = { name?: string; phone?: string; email?: string };

type StatusPanel = {
  type: "success" | "error";
  message: string;
  actionLabel?: string;
  onAction?: () => void;
};

export default function OverviewView({
  specialistName,
  specialistAvatar,
  services,
  dateLabel,
  timeLabel,
  phonePlaceholder,
  onBack,
  onEditSpecialist,
  onEditServices,
  onEditDateTime,

  // Controlled contact props + handlers
  name,
  phone,
  email,
  comment,
  confirmMethod,
  onChangeName,
  onChangePhone,
  onChangeEmail,
  onChangeComment,
  onChangeConfirmMethod,

  errors,
  onSubmit,
  isSubmitting,
  status,
  onOpenClientDashboard,
}: {
  specialistName: string;
  specialistAvatar?: string;
  services: { id: string; name: string; price: string; duration: string }[];
  dateLabel: string;
  timeLabel: string;
  phonePlaceholder: string;
  onBack: () => void;
  onEditSpecialist: () => void;
  onEditServices: () => void;
  onEditDateTime: () => void;

  // NEW
  name: string;
  phone: string;
  email?: string;
  comment?: string;
  confirmMethod: 1 | 2;
  onChangeName: (v: string) => void;
  onChangePhone: (v: string) => void;
  onChangeEmail: (v: string) => void;
  onChangeComment: (v: string) => void;
  onChangeConfirmMethod: (m: 1 | 2) => void;

  errors?: Errors;
  onSubmit: () => void;
  isSubmitting?: boolean;
  status?: StatusPanel;
  onOpenClientDashboard?: () => void;
}) {
  const { t } = useTranslation();
  // NEW: when the record is successfully created, show a full-screen centered success panel
  // and hide all other UI to avoid confusing the user.
  if (status?.type === "success") {
    return (
      <div className="bg-background h-[100dvh] min-h-[100dvh]">
        <div className="w-full h-full mx-auto sm:max-w-[420px] md:max-w-[520px]">
          <Card className="bg-card ios-shadow border-0 h-full flex flex-col sm:rounded-2xl sm:overflow-hidden">
            <div className="flex-1 flex items-center justify-center p-6">
              <div
                className="w-full max-w-sm rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-900 p-6 text-center"
                role="status"
                aria-live="polite"
              >
                <div className="flex items-center justify-center mb-3">
                  <CheckCircle2 className="w-8 h-8" aria-hidden="true" />
                </div>
                <div className="text-base font-semibold mb-1">{status.message}</div>
                {status.actionLabel && status.onAction ? (
                  <Button className="mt-3 rounded-lg" onClick={status.onAction}>
                    {status.actionLabel}
                  </Button>
                ) : null}

                {/* Account creation prompt */}
                {onOpenClientDashboard && (
                  <div className="mt-6 pt-4 border-t border-emerald-200">
                    <div className="text-sm text-emerald-800 mb-2">
                      {t("widget.overview.create_account_prompt", "Create an account to manage your bookings.")}
                    </div>
                    <Button
                      variant="outline"
                      className="rounded-lg border-emerald-400 text-emerald-800 hover:bg-emerald-100"
                      onClick={onOpenClientDashboard}
                    >
                      {t("widget.overview.create_account", "Create account")}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background h-[100dvh] min-h-[100dvh]">
      <div className="w-full h-full mx-auto sm:max-w-[420px] md:max-w-[520px]">
        <Card className="bg-card ios-shadow border-0 h-full flex flex-col sm:rounded-2xl sm:overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-border/10 flex items-center gap-4">
            <Button variant="ghost" size="sm" className="p-2 h-auto rounded-full" onClick={onBack}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h2 className="text-lg font-semibold">{t("widget.overview.title", "Review and confirm")}</h2>
          </div>

          {/* Scrollable body */}
          <div
            className="flex-1 overflow-y-auto p-6 space-y-4"
            style={{ paddingBottom: "calc(88px + env(safe-area-inset-bottom))" }}
          >
            {/* Specialist panel */}
            <div className="rounded-xl border border-border/50 bg-muted/30 p-4 flex items-center gap-3">
              <Avatar className="h-10 w-10 shrink-0">
                <AvatarImage src={specialistAvatar} />
                <AvatarFallback><User className="w-5 h-5" /></AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="text-xs text-muted-foreground">{t("widget.common.specialist", "Specialist")}</div>
                <div className="font-medium leading-tight">{specialistName}</div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full"
                onClick={onEditSpecialist}
                aria-label={t("widget.overview.edit_specialist", "Edit specialist")}
              >
                <Pencil className="w-4 h-4" />
              </Button>
            </div>

            {/* Date & time panel */}
            <div className="rounded-xl border border-border/50 bg-muted/30 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center ios-shadow shrink-0">
                <CalendarIcon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-xs text-muted-foreground">{t("widget.datetime.date_time", "Date and time")}</div>
                <div className="font-medium leading-tight">
                  {dateLabel} <span className="text-muted-foreground">•</span>{" "}
                  <Clock className="inline w-4 h-4 -mt-1 mr-1" />
                  {timeLabel}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full"
                onClick={onEditDateTime}
                aria-label={t("widget.overview.edit_datetime", "Edit date and time")}
              >
                <Pencil className="w-4 h-4" />
              </Button>
            </div>

            {/* Services panel */}
            <div className="rounded-xl border border-border/50 bg-muted/30 p-4">
              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">{t("widget.services.title", "Services")}</div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full"
                  onClick={onEditServices}
                  aria-label={t("widget.overview.edit_services", "Edit services")}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              </div>
              <ul className="mt-2 space-y-2">
                {services.map((s) => (
                  <li key={s.id} className="flex items-center gap-2 rounded-lg bg-background p-3 border">
                    <div className="flex-1">
                      <div className="font-medium text-sm">{s.name}</div>
                      <div className="text-xs text-muted-foreground">{s.duration}</div>
                    </div>
                    <div className="text-sm font-semibold">{s.price}</div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="ml-1 h-8 w-8 rounded-full"
                      onClick={onEditServices}
                      aria-label={t("widget.overview.edit_service", "Edit service {{name}}", { name: s.name })}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            </div>

            <Separator className="my-4" />

            {/* Contact details */}
            <div className="space-y-3">
              <div className="text-sm font-semibold">{t("widget.overview.contact_details", "Contact details")}</div>

              <div className="space-y-1.5">
                <Label className="flex gap-1 text-xs text-muted-foreground">
                  {t("widget.common.name", "Name")} <span className="text-destructive">*</span>
                </Label>
                <Input placeholder={t("widget.common.name_placeholder", "Your name")} value={name} onChange={(e) => onChangeName(e.target.value)} />
                {errors?.name && <p className="mt-1 text-xs text-destructive">{errors.name}</p>}
              </div>

              <div className="space-y-1.5">
                <Label className="flex gap-1 text-xs text-muted-foreground">
                  {t("widget.common.phone", "Phone")} <span className="text-destructive">*</span>
                </Label>
                <Input type="tel" placeholder={phonePlaceholder} value={phone} onChange={(e) => onChangePhone(e.target.value)} />
                {errors?.phone && <p className="mt-1 text-xs text-destructive">{errors.phone}</p>}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("widget.common.email_optional", "Email (optional)")}</Label>
                <Input
                  type="email"
                  placeholder={t("widget.common.email_placeholder", "example@email.com")}
                  value={email ?? ""}
                  onChange={(e) => onChangeEmail(e.target.value)}
                  className={errors?.email ? "border-red-500" : ""}
                  aria-invalid={Boolean(errors?.email)}
                />
                <p className="text-xs text-muted-foreground/70 italic">{t("widget.common.email_hint", "Receive booking details and reminders by email.")}</p>
                {errors?.email && <p className="mt-1 text-xs text-destructive">{errors.email}</p>}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("widget.overview.comment_optional", "Booking comment (optional)")}</Label>
                <Input placeholder={t("widget.overview.comment_placeholder", "Notes, preferences...")} value={comment ?? ""} onChange={(e) => onChangeComment(e.target.value)} />
              </div>

              <div>
                <Label className="mb-2 block text-xs text-muted-foreground">{t("widget.overview.confirmation", "Booking confirmation")}</Label>
                <div className="flex gap-2">
                  <label className={`flex-1 rounded-md p-3 border ${confirmMethod === 1 ? "border-primary bg-primary/5" : "border-border"}`}>
                    <input type="radio" name="confirmMethod" value="1" checked={confirmMethod === 1} onChange={() => onChangeConfirmMethod(1)} className="hidden" />
                    <div className="text-sm">{t("widget.common.phone", "Phone")}</div>
                  </label>
                  <label className={`flex-1 rounded-md p-3 border ${confirmMethod === 2 ? "border-primary bg-primary/5" : "border-border"}`}>
                    <input type="radio" name="confirmMethod" value="2" checked={confirmMethod === 2} onChange={() => onChangeConfirmMethod(2)} className="hidden" />
                    <div className="text-sm">{t("widget.overview.messenger", "Messenger")}</div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Sticky CTA / Status bar */}
          <div
            className="sticky bottom-0 inset-x-0 z-20 border-t border-border/10 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80"
            style={{ paddingBottom: "max(env(safe-area-inset-bottom), 16px)" }}
          >
            <div className="p-4 pt-3">
              {/* STATUS PANEL (optional) */}
              {status && (
                <div
                  className={[
                    "mb-3 rounded-xl border p-3 text-sm font-medium",
                    status.type === "success"
                      ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                      : "bg-red-50 border-red-200 text-red-800",
                  ].join(" ")}
                  role={status.type === "error" ? "alert" : "status"}
                  aria-live="polite"
                >
                  <div>{status.message}</div>
                  {status.actionLabel && status.onAction && (
                    <div className="mt-2">
                      <Button variant="outline" size="sm" className="rounded-lg" onClick={status.onAction}>
                        {status.actionLabel}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {!status && (
                <Button className="w-full h-12 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base shadow-lg" onClick={onSubmit} disabled={isSubmitting}>
                  {isSubmitting ? t("widget.common.submitting", "Submitting...") : t("widget.overview.submit", "Book now")}
                </Button>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
